import { Box, CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { PickerValidDate } from "@mui/x-date-pickers/models";
import dayjs from "dayjs";

const constainerStyles = {
  position: "relative",
  borderRadius: 1,
  border: "1px solid #cbcbcb",
  backgroundColor: "#fff",
  "&:hover": {
    borderColor: "#252525",
  },
  marginLeft: 0,
  width: "100%",
};

const iconWrapperStyles = {
  padding: 2,
  paddingLeft: 1.5,
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const inputBaseStyles = {
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: 1,
    paddingLeft: "3em",
    transition: "width 0.2s ease-in-out",
    width: "100%",
  },
};

export default function Filters({
  searchTerm,
  setSearchTerm,
  minDate,
  setMinDate,
  maxDate,
  setMaxDate,
  loading,
}: {
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  minDate: string;
  setMinDate: (minDate: string) => void;
  maxDate: string;
  setMaxDate: (maxDate: string) => void;
  loading?: boolean;
}) {
  // x-date-pickers types a picker's value as every adapter's date type
  // imported anywhere in the program. DateThresholdFilter pulls in
  // AdapterDateFns for its own subtree, so `Date` is in that union here even
  // though these two pickers sit under the app-wide AdapterDayjs and only
  // ever produce a Dayjs. dayjs() normalises either one.
  const onAcceptMinDate = (date: PickerValidDate | null) => {
    setMinDate(date ? dayjs(date).startOf("day").toISOString() : "");
  };

  const onAcceptMaxDate = (date: PickerValidDate | null) => {
    setMaxDate(date ? dayjs(date).endOf("day").toISOString() : "");
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 3,
      }}
    >
      <Box sx={constainerStyles}>
        <Box sx={iconWrapperStyles}>
          {loading ? <CircularProgress size={24} /> : <SearchIcon />}
        </Box>

        <InputBase
          placeholder="Search…"
          inputProps={{ "aria-label": "search", width: "100%" }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={inputBaseStyles}
        />
      </Box>

      <DatePicker
        label="Min date"
        value={minDate ? dayjs(minDate) : null}
        onAccept={onAcceptMinDate}
        maxDate={maxDate ? dayjs(maxDate).endOf("day") : dayjs()}
        slotProps={{
          textField: {
            size: "small",
          },
          actionBar: {
            actions: ["clear"],
          },
        }}
      />

      <DatePicker
        label="Max date"
        minDate={minDate ? dayjs(minDate).startOf("day") : undefined}
        maxDate={dayjs()}
        value={maxDate ? dayjs(maxDate) : null}
        onAccept={onAcceptMaxDate}
        slotProps={{
          textField: {
            size: "small",
          },
          actionBar: {
            actions: ["clear"],
          },
        }}
      />
    </Box>
  );
}
